import {
  clearEncryptedStorage,
  flushPendingPlainStorageKeyMigration,
  migrateAllPlainBrowserStorageKeys,
  NativeBrowserStorage,
  readEncryptedStorageItem,
  removeEncryptedStorageItem,
  writeEncryptedStorageItem,
} from './local-storage-encryption.util';
import { registerLocalStorageEncryptionKeyReadyHandler } from './local-storage-key-state.util';
import { bootstrapLocalStorageEncryptionFromMsal } from './local-storage-bootstrap.util';

/**
 * Create direct connection to real browser storage (skips our encryption layer).
 *
 * Parameter storage → window.localStorage or window.sessionStorage
 *
 * Why needed: when we save encrypted data we must use original browser functions.
 * If we use changed localStorage, it would encrypt again and again (loop).
 */
function createNativeBrowserStorage(storage: Storage): NativeBrowserStorage {
  return {
    // Original browser read — no encryption/decryption
    getItem: Storage.prototype.getItem.bind(storage),
    // Original browser save — no encryption
    setItem: Storage.prototype.setItem.bind(storage),
    // Original browser remove
    removeItem: Storage.prototype.removeItem.bind(storage),
    // Original browser clear all
    clear: Storage.prototype.clear.bind(storage),
    // Original browser list key by index
    key: Storage.prototype.key.bind(storage),
    // How many items are saved in this storage
    get length() {
      return storage.length;
    },
  };
}

// Saved connection to real localStorage (created once, reused)
let nativeLocalStorage: NativeBrowserStorage | null = null;
// Saved connection to real sessionStorage (created once, reused)
let nativeSessionStorage: NativeBrowserStorage | null = null;

/**
 * Get real localStorage (original browser, not our encryption version).
 * No parameters — uses window.localStorage
 * Used when writing encrypted data directly to browser.
 */
export function getNativeLocalStorage(): NativeBrowserStorage {
  // Create connection first time only
  nativeLocalStorage ??= createNativeBrowserStorage(window.localStorage);
  return nativeLocalStorage;
}

/**
 * Get real sessionStorage (original browser, not our encryption version).
 * No parameters — uses window.sessionStorage
 */
export function getNativeSessionStorage(): NativeBrowserStorage {
  nativeSessionStorage ??= createNativeBrowserStorage(window.sessionStorage);
  return nativeSessionStorage;
}

/**
 * Migration 4.0.0 — Convert old plain data in localStorage and sessionStorage.
 *
 * No parameters — uses getNativeLocalStorage and getNativeSessionStorage
 * Called after login, page refresh, or when password becomes ready.
 */
export function migrateAllPlainLocalStorageKeys(): void {
  // Only run in browser (not during server build)
  if (typeof window === 'undefined') {
    return;
  }

  // Convert plain keys in both storages
  migrateAllPlainBrowserStorageKeys(
    getNativeLocalStorage(),
    getNativeSessionStorage()
  );
}

/**
 * Setup 0.0.3 — Run when secret password becomes ready.
 *
 * No parameters — called automatically by setLocalStorageEncryptionKey
 * Converts old plain localStorage data and any data saved before password was ready.
 */
function handleEncryptionKeyReady(): void {
  // Convert all plain keys to encrypted
  migrateAllPlainLocalStorageKeys();
  // Handle data that was saved as plain text before login finished
  flushPendingPlainStorageKeyMigration(
    getNativeLocalStorage(),
    getNativeSessionStorage()
  );
}

/**
 * Setup 0.0.2 — Connect encryption to browser save and read.
 *
 * Called by: installLocalStorageEncryptionShim (once when app starts)
 *
 * ── PARAMETER 1: storage ──
 * Where it comes from:
 *   - window.localStorage  (passed as 'local' kind) — data stays after browser close
 *   - window.sessionStorage (passed as 'session' kind) — data clears when tab closes
 * What it is: browser's storage box on the user's computer
 *
 * ── PARAMETER 2: kind ──
 * Where it comes from: installLocalStorageEncryptionShim sends 'local' or 'session'
 * What it is: tells our read/save functions which storage type is being used
 * How it flows: passed into readEncryptedStorageItem and writeEncryptedStorageItem
 *
 * ── STEP BY STEP ──
 * Step 1: Create a direct line to real browser storage (variable: native)
 * Step 2: When screen reads data  → run readEncryptedStorageItem  (decrypt, return normal value)
 * Step 3: When screen saves data  → run writeEncryptedStorageItem (encrypt, save to browser)
 * Step 4: When screen deletes key → run removeEncryptedStorageItem
 * Step 5: When screen clears all  → run clearEncryptedStorage
 */
function patchStorage(storage: Storage, kind: 'local' | 'session'): void {

  // Step 1 — Create direct connection to real browser storage.
  // storage = browser storage box (from parameter 1 above)
  // native  = same storage but using original browser functions (no encryption loop)
  // native is passed to all save/read functions so they can read/write encrypted data directly
  const native = createNativeBrowserStorage(storage);

  // Step 2 — When any screen calls localStorage.getItem("hawbId"):
  //   key   → comes from screen code, example "hawbId"
  //   native → direct browser storage (from Step 1)
  //   kind  → 'local' or 'session' (from parameter 2)
  //   Flow: key goes to readEncryptedStorageItem → gets password from memory →
  //         decrypts value from browser → returns normal value like "12345" to screen
  storage.getItem = (key: string): string | null =>
    readEncryptedStorageItem(native, kind, key);

  // Step 3 — When any screen calls localStorage.setItem("hawbId", "12345"):
  //   key   → comes from screen code, example "hawbId"
  //   value → comes from screen code, example "12345"
  //   native → direct browser storage (from Step 1)
  //   kind  → 'local' or 'session' (from parameter 2)
  //   Flow: key + value go to writeEncryptedStorageItem → gets password from memory →
  //         hides name "hawbId", encrypts value "12345" → saves to browser
  storage.setItem = (key: string, value: string): void =>
    writeEncryptedStorageItem(native, kind, key, value);

  // Step 4 — When any screen calls localStorage.removeItem("hawbId"):
  //   key   → comes from screen code, example "hawbId"
  //   native → direct browser storage (from Step 1)
  //   kind  → 'local' or 'session' (from parameter 2)
  //   Flow: removes hidden name and plain name both from browser
  storage.removeItem = (key: string): void =>
    removeEncryptedStorageItem(native, kind, key);

  // Step 5 — When any screen calls localStorage.clear():
  //   native → direct browser storage (from Step 1)
  //   Flow: removes all PacTrak app keys, keeps Microsoft login keys (msal.*)
  storage.clear = (): void => clearEncryptedStorage(native);
}

// True after encryption is connected — runs only once
let shimInstalled = false;

/**
 * Setup 0.0.1 — Turn on automatic encryption for localStorage and sessionStorage.
 *
 * Called by: main.ts (first thing before Angular app starts)
 * No parameters.
 *
 * STEP BY STEP:
 *   Step 1: If already done or not in browser → stop
 *   Step 2: Register handleEncryptionKeyReady — runs when user logs in and password is ready
 *   Step 3: Connect encryption to localStorage  (storage = window.localStorage,  kind = 'local')
 *   Step 4: Connect encryption to sessionStorage (storage = window.sessionStorage, kind = 'session')
 *   Step 5: Mark as done so this does not run again
 *
 * After this, every screen in the app can use normal localStorage.setItem/getItem.
 * They do not need to know about encryption — it happens automatically in the background.
 */
export function installLocalStorageEncryptionShim(): void {
  if (shimInstalled || typeof window === 'undefined') {
    return;
  }

  // Step 2 — when secret password is saved in memory after login, convert old plain data
  registerLocalStorageEncryptionKeyReadyHandler(handleEncryptionKeyReady);
  // Step 3 — connect to localStorage (data stays after browser close)
  patchStorage(window.localStorage, 'local');
  // Step 4 — connect to sessionStorage (data clears when tab closes)
  patchStorage(window.sessionStorage, 'session');
  // Step 5
  shimInstalled = true;
}

// Re-export so main.ts can import both functions from one file
export { bootstrapLocalStorageEncryptionFromMsal };
